import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, SegmentedControl, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { ago, compact, errorKindNames, fmtDateTime, fmtTime, num, serviceNames, serviceTone, taskTypeNames } from "../lib/format";
import { useAction, useAdminMachines } from "../lib/queries";
import type { Me } from "../lib/types";
import { Meter, Meters, SeriesChart, tone } from "../charts";
import { Empty, ErrorBox, InfoRow, Loading, Panel, StatGrid } from "../console/ui";
import { eventText, MachineIncidents, type StatusEvent } from "./incidents";
import { DataHeading } from "../console/freshness";

/* ---------- 집계 API 타입 (server apps/api/src/biz/stats) ---------- */

type Range = "24h" | "7d" | "30d";
export type Stats = {
  range: Range;
  step: "hour" | "day";
  labels: string[];
  runs: { succeeded: number[]; failed: number[]; cancelled: number[]; held: number[] };
  tokens: { input: number[]; cached: number[]; output: number[]; reasoning: number[]; counted: number[] };
  timing: { avgRunSec: (number | null)[]; avgWaitSec: (number | null)[] };
  disconnects: number[];
  totals: {
    succeeded: number;
    failed: number;
    cancelled: number;
    successRatePct: number | null;
    tokens: { input: number; cached: number; output: number; reasoning: number; counted: number };
    cacheHitPct: number | null;
    disconnects: number;
  };
  errors: { errorKind: string; n: number }[];
  types: { type: string; total: number; succeeded: number }[];
  now: {
    machinesOnline?: number;
    machinesPaired?: number;
    devicesReady?: number;
    devicesAttached?: number;
    tasksQueued?: number;
    tasksRunning?: number;
    runsHeld?: number;
    oldestQueuedSec?: number | null;
    llmReady?: number;
    llmThrottled?: number;
  };
};
type CustomerStat = { customerId: string; customerName: string; status: keyof typeof serviceNames; succeeded: number; failed: number; tokens: number; machinesOnline: number; disconnects: number };

export const useStats = (range: Range, customerId?: string) =>
  useQuery({
    queryKey: ["stats", customerId ?? "admin", range],
    queryFn: () => api.get<Stats>(customerId ? `/c/${customerId}/stats?range=${range}` : `/admin/stats?range=${range}`),
    refetchInterval: 30_000,
  });
const useCustomerStats = (range: Range) =>
  useQuery({ queryKey: ["stats", "customers", range], queryFn: () => api.page<CustomerStat>(`/admin/stats/customers?range=${range}`), refetchInterval: 60_000 });

const RANGES: { value: Range; label: string }[] = [
  { value: "24h", label: "24시간" },
  { value: "7d", label: "7일" },
  { value: "30d", label: "30일" },
];
export function RangePicker({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return <SegmentedControl label="기간" value={value} onChange={onChange} options={RANGES} />;
}
const sec = (v: number | null | undefined) => (v === null || v === undefined ? "-" : v < 60 ? `${v}초` : `${Math.floor(v / 60)}분 ${v % 60}초`);
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : null);

/* ---------- 브릿지 상태 API 타입 (server apps/api/src/biz/machine-state) ---------- */

export type Readiness = "unpaired" | "paused" | "offline" | "llm_not_installed" | "llm_login_required" | "llm_throttled" | "no_device" | "device_attention" | "ready";
export const readinessText: Record<Readiness, [string, string]> = {
  ready: ["작업 가능", "teal"],
  unpaired: ["미연결", "neutral"],
  paused: ["배정 중지", "amber"],
  offline: ["연결 끊김", "coral"],
  llm_not_installed: ["AI 설치 필요", "coral"],
  llm_login_required: ["AI 로그인 필요", "amber"],
  llm_throttled: ["AI 한도 도달", "amber"],
  no_device: ["폰 없음", "amber"],
  device_attention: ["폰 확인 필요", "amber"],
};
export const useStatusHistory = (path: string, hours = 24) =>
  useQuery({ queryKey: ["status-history", path, hours], queryFn: () => api.page<StatusEvent>(`${path}?hours=${hours}`), refetchInterval: 30_000 });

export function StatusHistoryPanel({ path, title = "연결 이력 · 24시간", showOwner = false }: { path: string; title?: string; showOwner?: boolean }) {
  const q = useStatusHistory(path);
  const rows = q.data?.items ?? [];
  return (
    <Panel title={title} extra={<Badge>{rows.length}건</Badge>}>
      {q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <InfoRow title="변화 없음" description="연결·AI·폰 상태가 바뀌면 여기에 기록됩니다." />
      ) : (
        rows.slice(0, 50).map((e, i) => (
          <InfoRow key={`${e.at}-${i}`} title={`${fmtTime(e.at)} · ${eventText(e)}`} description={showOwner ? `${e.customerName} · ${e.machineLabel}` : e.machineLabel} />
        ))
      )}
    </Panel>
  );
}

/* ---------- 공통 차트 묶음 ---------- */

// 구간별 건수는 누적 막대(구간 합계가 곧 처리량), 시간은 선(작업이 없던 구간은 비움)
function RunCharts({ s }: { s: Stats }) {
  return (
    <>
      <SeriesChart
        title="작업 결과"
        description="구간별 끝난 작업 · 막대 높이 = 처리한 작업 수"
        kind="bar"
        stacked
        labels={s.labels}
        series={[
          { name: "성공", values: s.runs.succeeded, color: tone.ok },
          { name: "실패", values: s.runs.failed, color: tone.fail },
          { name: "취소", values: s.runs.cancelled, color: tone.muted },
          { name: "보류", values: s.runs.held, color: tone.warn },
        ]}
      />
      <SeriesChart
        title="대기·실행 시간 (평균)"
        description="작업이 없던 구간은 비워 둡니다"
        labels={s.labels}
        format="seconds"
        series={[
          { name: "배정까지 대기", values: s.timing.avgWaitSec },
          { name: "실행", values: s.timing.avgRunSec },
        ]}
      />
    </>
  );
}

function FailurePanels({ s }: { s: Stats }) {
  return (
    <div className="meta-crew-grid-main">
      {s.errors.length === 0 ? (
        <Panel title="실패 원인">
          <InfoRow title="이 기간 실패 없음" />
        </Panel>
      ) : (
        // 원인별 건수 순위 → 가로 막대 (이름이 길어도 읽힘)
        <SeriesChart
          title="실패 원인"
          description={`이 기간 실패 ${s.totals.failed}건`}
          kind="bar"
          horizontal
          labels={[...s.errors].sort((a, b) => b.n - a.n).map((e) => errorKindNames[e.errorKind] ?? e.errorKind)}
          series={[{ name: "실패", values: [...s.errors].sort((a, b) => b.n - a.n).map((e) => e.n), color: tone.fail }]}
        />
      )}
      <Panel title="작업 유형별">
        {s.types.length === 0 ? (
          <InfoRow title="이 기간 작업 없음" />
        ) : (
          s.types.map((t) => (
            <InfoRow key={t.type} title={taskTypeNames[t.type] ?? t.type} description={`${t.total}건 · 성공 ${t.succeeded}건`}>
              <Badge tone={pct(t.succeeded, t.total)! >= 90 ? "teal" : "amber"}>{pct(t.succeeded, t.total)}%</Badge>
            </InfoRow>
          ))
        )}
      </Panel>
    </div>
  );
}

function TokenChart({ s }: { s: Stats }) {
  return (
    <SeriesChart
      title="토큰 사용 추이"
      description="Codex 가 작업마다 보고한 값의 합계 · 막대 높이 = 전체 토큰"
      kind="bar"
      stacked
      format="compact"
      labels={s.labels}
      series={[
        { name: "입력 (캐시 제외)", values: s.tokens.input.map((v, i) => Math.max(0, v - (s.tokens.cached[i] ?? 0))) },
        { name: "캐시 입력", values: s.tokens.cached },
        { name: "출력", values: s.tokens.output },
        { name: "추론", values: s.tokens.reasoning },
      ]}
    />
  );
}

/* ---------- 전체 관리: 모니터링 > 시스템 지표 ---------- */

export function AdminMonitoringScreen() {
  const [range, setRange] = useState<Range>("24h");
  const q = useStats(range);
  const machines = useAdminMachines();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  const s = q.data;
  const n = s.now;
  const paired = n.machinesPaired ?? 0;
  return (
    <>
      <RangePicker value={range} onChange={setRange} />
      <DataHeading title="시스템 지표" />
      <MachineIncidents />
      <Meters>
        <Meter label="온라인 머신" value={pct(n.machinesOnline ?? 0, paired)} inverse warning={90} danger={60} note={`${n.machinesOnline ?? 0} / ${paired}대`} />
        <Meter label="AI 사용 가능 머신" value={pct(n.llmReady ?? 0, n.machinesOnline ?? 0)} inverse warning={90} danger={60} note={`${n.llmReady ?? 0}대 · 한도 도달 ${n.llmThrottled ?? 0}대`} />
        <Meter label="작업 가능 폰" value={pct(n.devicesReady ?? 0, n.devicesAttached ?? 0)} inverse warning={90} danger={60} note={`${n.devicesReady ?? 0} / ${n.devicesAttached ?? 0}대`} />
        <Meter label="성공률" value={s.totals.successRatePct} inverse warning={95} danger={80} note={`성공 ${s.totals.succeeded} · 실패 ${s.totals.failed}`} />
        <Meter label="대기 작업" value={n.tasksQueued ?? 0} unit="건" warning={10} danger={30} max={50} note={`진행 중 ${n.tasksRunning ?? 0} · 보류 ${n.runsHeld ?? 0}`} />
        <Meter label="가장 오래 대기" value={n.oldestQueuedSec ?? 0} unit="초" warning={300} danger={1800} max={3600} note={n.oldestQueuedSec ? sec(n.oldestQueuedSec) : "대기 없음"} />
      </Meters>
      <RunCharts s={s} />
      <SeriesChart title="머신 연결 끊김" description="구간별 끊김 횟수 (모든 머신 합계)" kind="bar" labels={s.labels} series={[{ name: "끊김", values: s.disconnects, color: tone.fail }]} />
      <StatusHistoryPanel path="/admin/machines/status-history" title="상태 변화 · 24시간" showOwner />
      <FailurePanels s={s} />
      <Panel title="머신별 지금 상태">
        <MachineStateTable rows={machines.data?.items ?? []} />
      </Panel>
    </>
  );
}

function MachineStateTable({ rows }: { rows: { id: string; customerName?: string; label: string; connected: boolean; pairedAt: string | null; version: string | null; lastSeenAt: string | null; lastState: Record<string, any> | null; deviceCount?: number }[] }) {
  if (!rows.length) return <Empty>등록된 머신이 없습니다.</Empty>;
  return (
    <Table>
      <thead>
        <tr>
          <th>머신</th>
          <th>서버</th>
          <th>AI</th>
          <th>폰</th>
          <th>프로그램</th>
        </tr>
      </thead>
      <tbody>
        {rows
          .filter((m) => m.pairedAt)
          .map((m) => {
            const st = m.lastState ?? {};
            const llm = st.llm ?? {};
            const throttled = llm.throttledUntil && new Date(llm.throttledUntil).getTime() > Date.now();
            const bridge = st.bridge ?? {};
            return (
              <tr key={m.id}>
                <td>
                  {m.label}
                  <small className="meta-crew-sub">{m.customerName}</small>
                </td>
                <td>
                  <Badge tone={m.connected ? "teal" : "coral"}>{m.connected ? "연결됨" : "끊김"}</Badge>
                  <small className="meta-crew-sub">{ago(m.lastSeenAt)}</small>
                </td>
                <td>
                  <Badge tone={throttled ? "amber" : llm.available ? "teal" : "amber"}>{throttled ? "한도 도달" : llm.available ? "사용 가능" : llm.loggedIn === false ? "로그인 필요" : "확인 필요"}</Badge>
                  <small className="meta-crew-sub">{llm.version ? `Codex ${llm.version}` : ""}</small>
                </td>
                <td>{m.deviceCount ?? st.devices ?? 0}대</td>
                <td>
                  {m.version ? `v${m.version}` : "-"}
                  <small className="meta-crew-sub">
                    {bridge.uptimeSec ? `가동 ${Math.round(bridge.uptimeSec / 3600)}시간` : ""}
                    {bridge.outboxPending ? ` · 미전송 ${bridge.outboxPending}` : ""}
                    {bridge.socketReconnects24h ? ` · 재연결 ${bridge.socketReconnects24h}회` : ""}
                  </small>
                </td>
              </tr>
            );
          })}
      </tbody>
    </Table>
  );
}

/* ---------- 전체 관리: 모니터링 > LLM 사용량 ---------- */

export function AdminLlmScreen() {
  const [range, setRange] = useState<Range>("7d");
  const q = useStats(range);
  const per = useCustomerStats(range);
  const machines = useAdminMachines();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} />;
  const s = q.data;
  const t = s.totals.tokens;
  const ms = (machines.data?.items ?? []).filter((m) => m.pairedAt);
  return (
    <>
      <RangePicker value={range} onChange={setRange} />
      <StatGrid
        items={[
          ["LLM 작업", num(t.counted), "산입된 작업 수"],
          ["토큰 사용", compact(t.input + t.output + t.reasoning), `입력 ${compact(t.input)} · 출력 ${compact(t.output)}`],
          ["캐시 적중", s.totals.cacheHitPct === null ? "-" : `${s.totals.cacheHitPct}%`, "입력 중 캐시 비율", "teal"],
          ["한도 도달 머신", s.now.llmThrottled ?? 0, "지금 기준", (s.now.llmThrottled ?? 0) > 0 ? "amber" : "teal"],
        ]}
      />
      <TokenChart s={s} />
      <div className="meta-crew-grid-main">
        <CustomerTokens items={per.data?.items ?? []} />
        <Panel title="머신별 AI 상태">
          {ms.length === 0 && <InfoRow title="연결된 머신이 없습니다" />}
          {ms.map((m) => {
            const llm = (m.lastState as Record<string, any> | null)?.llm ?? {};
            const throttled = llm.throttledUntil && new Date(llm.throttledUntil).getTime() > Date.now();
            return (
              <InfoRow
                key={m.id}
                title={`${m.customerName} · ${m.label}`}
                description={throttled ? `한도 도달 · ${fmtDateTime(llm.throttledUntil)} 복구 예상` : llm.lastLimitAt ? `마지막 한도 도달 ${fmtDateTime(llm.lastLimitAt)}` : llm.version ? `Codex ${llm.version}` : "보고 없음"}
              >
                <Badge tone={throttled ? "amber" : llm.available ? "teal" : "amber"}>{throttled ? "한도 도달" : llm.available ? "사용 가능" : "확인 필요"}</Badge>
              </InfoRow>
            );
          })}
        </Panel>
      </div>
    </>
  );
}

/** 고객사별 토큰 순위 (가로 막대, 사용한 곳만) */
function CustomerTokens({ items }: { items: { customerId: string; customerName: string; tokens: number; succeeded: number }[] }) {
  const used = items.filter((c) => c.tokens > 0).sort((a, b) => b.tokens - a.tokens);
  if (!used.length)
    return (
      <Panel title="고객사별 토큰">
        <InfoRow title="이 기간 토큰 사용 없음" />
      </Panel>
    );
  return <SeriesChart title="고객사별 토큰" description="이 기간 합계 · 많은 순" kind="bar" horizontal format="compact" labels={used.map((c) => c.customerName)} series={[{ name: "토큰", values: used.map((c) => c.tokens) }]} />;
}

/* ---------- 전체 관리: 모니터링 > 전체 사용량 (고객사 비교) ---------- */

export function AdminUsageCompare() {
  const [range, setRange] = useState<Range>("30d");
  const q = useStats(range);
  const per = useCustomerStats(range);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={errorMessage(q.error)} />;
  return (
    <>
      <RangePicker value={range} onChange={setRange} />
      <SeriesChart title="산입 작업 추이" description="한도에 산입된 작업 수" kind="bar" labels={q.data.labels} series={[{ name: "산입 작업", values: q.data.tokens.counted }]} />
      <Panel title="고객사별">
        <Table>
          <thead>
            <tr>
              <th>고객사</th>
              <th>성공</th>
              <th>실패</th>
              <th>토큰</th>
              <th>온라인 머신</th>
              <th>연결 끊김</th>
            </tr>
          </thead>
          <tbody>
            {(per.data?.items ?? []).map((c) => (
              <tr key={c.customerId}>
                <td>
                  {c.customerName}
                  <small className="meta-crew-sub">
                    <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
                  </small>
                </td>
                <td>{num(c.succeeded)}</td>
                <td>{num(c.failed)}</td>
                <td>{compact(c.tokens)}</td>
                <td>{c.machinesOnline}</td>
                <td>{c.disconnects}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </>
  );
}

/* ---------- 고객 화면 ---------- */

/** 현황 화면의 "작업 결과" 차트 (자기 고객사, 24시간). */
export function CustomerResultsPanel({ customerId }: { customerId: string }) {
  const q = useStats("24h", customerId);
  if (!q.data) return null;
  const s = q.data;
  return (
    <SeriesChart
      title="24시간 작업 결과"
      description={`성공 ${s.totals.succeeded} · 실패 ${s.totals.failed}`}
      kind="bar"
      stacked
      labels={s.labels}
      series={[
        { name: "성공", values: s.runs.succeeded, color: tone.ok },
        { name: "실패", values: s.runs.failed, color: tone.fail },
      ]}
    />
  );
}

/** 사용량 화면의 추이 차트 (자기 고객사). */
export function CustomerUsageCharts({ customerId }: { customerId: string }) {
  const [range, setRange] = useState<Range>("30d");
  const q = useStats(range, customerId);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={errorMessage(q.error)} />;
  const s = q.data;
  return (
    <>
      <RangePicker value={range} onChange={setRange} />
      <SeriesChart title="산입 작업" description="한도에 산입된 작업 수" kind="bar" labels={s.labels} series={[{ name: "산입 작업", values: s.tokens.counted }]} />
      <TokenChart s={s} />
      <RunCharts s={s} />
      <FailurePanels s={s} />
    </>
  );
}

/** 머신 상세의 실시간 상태: 저장된 상태 + 지금 다시 묻기 (코어 → 브릿지 → 코어). */
export function MachineLiveState({ me, customerId, machineId }: { me: Me; customerId: string; machineId: string }) {
  const base = `/c/${customerId}/machines/${machineId}`;
  const state = useQuery({ queryKey: ["machine-state", machineId], queryFn: () => api.get<{ readiness: Readiness; issues: Readiness[]; state: Record<string, any> | null }>(`${base}/state`), refetchInterval: 15_000 });
  const [snap, setSnap] = useState<Record<string, any> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = useAction(() => api.post<{ snapshot?: Record<string, any>; error?: string; at?: string | null }>(`${base}/state/refresh`), [["machine-state", machineId]]);
  const canRefresh = can(me, "MACHINE_COMMAND", "CONSOLE_READ");
  const r = state.data?.readiness;
  const s = snap ?? state.data?.state ?? {};
  // 스냅숏은 devices 가 목록, 저장된 상태는 devices 가 개수이고 목록은 deviceStates.
  const devices: Record<string, any>[] = Array.isArray(s.devices) ? s.devices : Array.isArray(s.deviceStates) ? s.deviceStates : [];
  return (
    <>
      <Panel
        title="준비 상태"
        extra={
          canRefresh ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={refresh.isPending}
              onClick={() =>
                void refresh
                  .mutateAsync(undefined)
                  .then((x) => {
                    if (x.error) setMsg(x.error === "offline" ? "머신이 서버에 연결되어 있지 않습니다." : "머신이 응답하지 않았습니다 (이전 버전 프로그램일 수 있음).");
                    else {
                      setSnap(x.snapshot ?? null);
                      setMsg(`방금 받은 상태입니다 (${fmtTime(new Date().toISOString())}).`);
                    }
                  })
                  .catch((e) => setMsg(errorMessage(e)))
              }
            >
              지금 상태 확인
            </Button>
          ) : undefined
        }
      >
        {r && (
          <InfoRow title={readinessText[r][0]} description={(state.data?.issues ?? []).filter((i) => i !== r).map((i) => readinessText[i][0]).join(" · ") || "중앙이 이 상태로 작업 배정을 판단합니다."}>
            <Badge tone={readinessText[r][1]}>{readinessText[r][0]}</Badge>
          </InfoRow>
        )}
        {msg && <InfoRow title={msg} />}
        {s.bridge && (
          <InfoRow
            title={`프로그램 ${s.bridge.appVersion ? `v${s.bridge.appVersion}` : ""}${s.bridge.engineVersion ? ` · 엔진 ${s.bridge.engineVersion}` : ""}`}
            description={`가동 ${s.bridge.uptimeSec ? Math.round(s.bridge.uptimeSec / 3600) : 0}시간 · 24시간 재연결 ${s.bridge.socketReconnects24h ?? 0}회 · 미전송 이벤트 ${s.bridge.outboxPending ?? 0}건${s.diskFreeGb !== undefined ? ` · 디스크 여유 ${s.diskFreeGb}GB` : ""}`}
          />
        )}
        {s.llm && (
          <InfoRow
            title={`AI · ${s.llm.available ? "사용 가능" : s.llm.loggedIn === false ? "로그인 필요" : s.llm.installed === false ? "설치 필요" : "확인 필요"}`}
            description={[s.llm.version ? `Codex ${s.llm.version}` : "", s.llm.throttledUntil ? `한도 도달 · ${fmtDateTime(s.llm.throttledUntil)} 복구 예상` : "", s.llm.lastLimitAt ? `마지막 한도 ${fmtDateTime(s.llm.lastLimitAt)}` : ""].filter(Boolean).join(" · ") || "보고 없음"}
          />
        )}
        {devices.map((d) => (
          <InfoRow
            key={d.serial}
            title={`${d.model ?? d.serial}`}
            description={[
              d.state === "device" ? "승인됨" : d.state === "unauthorized" ? "USB 승인 필요" : d.state,
              d.batteryPct !== undefined ? `배터리 ${d.batteryPct}%${d.charging ? " 충전 중" : ""}` : "",
              d.screenOn === false ? "화면 꺼짐" : "",
              d.locked ? "잠김" : "",
              d.keyboardReady === false ? "한글 키보드 없음" : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            <Badge tone={d.state === "device" && d.keyboardReady !== false ? "teal" : "amber"}>{d.state === "device" ? "준비" : "확인"}</Badge>
          </InfoRow>
        ))}
        {Array.isArray(s.recentErrors) && s.recentErrors.length > 0 && (
          <details className="mcc-details">
            <summary>최근 엔진 오류 (관리자용)</summary>
            <pre className="mcc-pre">{s.recentErrors.join("\n")}</pre>
          </details>
        )}
      </Panel>
      <StatusHistoryPanel path={`${base}/status-history`} />
    </>
  );
}
